import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface DoctorFrequentTemplate {
  id: string;
  label: string;
  lines: readonly {
    item_kind: 'product' | 'service' | 'kit';
    catalog_id: string;
    item_code: string;
    item_name: string;
    quantity: string;
  }[];
}

interface DoctorPosState {
  active_draft_id: string | null;
  frequent_templates: readonly DoctorFrequentTemplate[];
  setActiveDraft: (id: string | null) => void;
  upsertTemplate: (tpl: DoctorFrequentTemplate) => void;
  removeTemplate: (id: string) => void;
}

const MAX_TEMPLATES = 6;

/**
 * Persisted across reloads + inactivity locks so doctor never loses the active draft
 * id or their personal "Frequent treatments" panel (rule §28 — drafts survive lock).
 */
export const useDoctorPosStore = create<DoctorPosState>()(
  persist(
    (set) => ({
      active_draft_id: null,
      frequent_templates: [],
      setActiveDraft: (id) => set({ active_draft_id: id }),
      upsertTemplate: (tpl) =>
        set((s) => {
          const without = s.frequent_templates.filter((t) => t.id !== tpl.id);
          const next = [tpl, ...without].slice(0, MAX_TEMPLATES);
          return { frequent_templates: next };
        }),
      removeTemplate: (id) =>
        set((s) => ({ frequent_templates: s.frequent_templates.filter((t) => t.id !== id) })),
    }),
    {
      name: 'totem:doctor_pos',
      partialize: (s) => ({
        active_draft_id: s.active_draft_id,
        frequent_templates: s.frequent_templates,
      }),
    },
  ),
);
